import { NError, NSuccess } from '@src/configs/const.config'
import { TResApi, TResApiErr, TResDataListApi } from '@src/configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import {
  TCatalogColor,
  TCatalogItem,
  TCatalogItemGroup,
  TCreateItemCodeGroup,
  TItemCodeGroup,
  TItemCodeGroupDetail,
  TItemCodeGroupMember,
  TItemCodeGroupOverlapsResult,
  TPutItemCodeGroupMembers,
  TPutItemCodeGroupMembersResult,
  TQueryCatalogItems,
  TQueryItemCodeGroup,
  TUpdateItemCodeGroup,
} from '@src/modules'
import { notification } from 'antd'
import { useMutation, useQuery } from 'react-query'

import {
  createItemCodeGroup,
  deleteItemCodeGroup,
  getCatalogColors,
  getCatalogItemGroups,
  getCatalogItems,
  getItemCodeGroupDetail,
  getItemCodeGroupMembers,
  getItemCodeGroupOverlaps,
  getItemCodeGroups,
  putItemCodeGroupMembers,
  updateItemCodeGroup,
} from '../apis/item-code-group-admin'

import { queryClient } from '..'

export const ITEM_CODE_GROUP_LIST = 'ITEM_CODE_GROUP_LIST'
export const ITEM_CODE_GROUP_DETAIL = 'ITEM_CODE_GROUP_DETAIL'
export const ITEM_CODE_GROUP_MEMBERS = 'ITEM_CODE_GROUP_MEMBERS'
export const ITEM_CODE_GROUP_CATALOG = 'ITEM_CODE_GROUP_CATALOG'
export const ITEM_CODE_GROUP_CATALOG_COLORS = 'ITEM_CODE_GROUP_CATALOG_COLORS'
export const ITEM_CODE_GROUP_CATALOG_ITEMS = 'ITEM_CODE_GROUP_CATALOG_ITEMS'
export const ITEM_CODE_GROUP_OVERLAPS = 'ITEM_CODE_GROUP_OVERLAPS'

// `/catalog-item-groups` and `/{id}/members` deliberately diverge from the shared list envelope
// (yaml x-dev-notes): { data, message, total } with no limit/page — both are closed ~34-row sets.
type TResSetApi<T> = {
  data: T
  message: string
  total: number
}

const invalidateGroupList = () => queryClient.invalidateQueries([ITEM_CODE_GROUP_LIST])

// A member write changes memberCount/itemCount/staleMemberCount (detail + list badges) for this
// group, and assignedToGroupIds (overlap warning) for every OTHER group's catalog view — all are
// projections of the same table.
const invalidateAfterMemberWrite = (id: number) => {
  queryClient.invalidateQueries([ITEM_CODE_GROUP_MEMBERS, id])
  queryClient.invalidateQueries([ITEM_CODE_GROUP_DETAIL, id])
  queryClient.invalidateQueries([ITEM_CODE_GROUP_CATALOG])
  queryClient.invalidateQueries([ITEM_CODE_GROUP_CATALOG_COLORS])
  queryClient.invalidateQueries([ITEM_CODE_GROUP_CATALOG_ITEMS])
  queryClient.invalidateQueries([ITEM_CODE_GROUP_OVERLAPS])
  invalidateGroupList()
}

export const formatApiError = (message: string | string[] | undefined): string =>
  Array.isArray(message) ? message.join('; ') : message ?? NError

const notifyApiError = (error: TResApiErr) =>
  notification.error({ message: NError, description: formatApiError(error?.message) })

export const useItemCodeGroups = (params: TQueryItemCodeGroup) => {
  const token = checkAuth()
  return useQuery<TResDataListApi<TItemCodeGroup[]>>(
    [ITEM_CODE_GROUP_LIST, params],
    () => getItemCodeGroups(params, token),
    { enabled: !!token, keepPreviousData: true },
  )
}

export const useItemCodeGroup = (id: number | undefined) => {
  const token = checkAuth()
  return useQuery<TResApi<TItemCodeGroupDetail>>(
    [ITEM_CODE_GROUP_DETAIL, id],
    () => getItemCodeGroupDetail(id as number, token),
    { enabled: !!token && !!id },
  )
}

export const useItemCodeGroupMembers = (id: number | undefined) => {
  const token = checkAuth()
  return useQuery<TResSetApi<TItemCodeGroupMember[]>>(
    [ITEM_CODE_GROUP_MEMBERS, id],
    () => getItemCodeGroupMembers(id as number, token),
    { enabled: !!token && !!id },
  )
}

// `enabled` lets a caller that only needs ONE axis's catalog (e.g. useOverlapLookup, which knows
// the group's memberType) skip fetching the other two — default true keeps every existing call site
// (which always wants the data) unchanged.
export const useCatalogItemGroups = (enabled = true) => {
  const token = checkAuth()
  return useQuery<TResSetApi<TCatalogItemGroup[]>>(
    [ITEM_CODE_GROUP_CATALOG],
    () => getCatalogItemGroups(token),
    { enabled: !!token && enabled },
  )
}

export const useCatalogColors = (search: string | undefined, enabled = true) => {
  const token = checkAuth()
  return useQuery<TResSetApi<TCatalogColor[]>>(
    [ITEM_CODE_GROUP_CATALOG_COLORS, search],
    () => getCatalogColors(search, token),
    { enabled: !!token && enabled },
  )
}

export const useCatalogItems = (params: TQueryCatalogItems) => {
  const token = checkAuth()
  return useQuery<TResDataListApi<TCatalogItem[]>>(
    [ITEM_CODE_GROUP_CATALOG_ITEMS, params],
    () => getCatalogItems(params, token),
    { enabled: !!token, keepPreviousData: true },
  )
}

// `< 2` groupIds is a client-side no-op (BE would return total:0 anyway) — skip the round-trip.
export const useItemCodeGroupOverlaps = (groupIds: number[]) => {
  const token = checkAuth()
  return useQuery<TResApi<TItemCodeGroupOverlapsResult>>(
    [ITEM_CODE_GROUP_OVERLAPS, groupIds],
    () => getItemCodeGroupOverlaps(groupIds, token),
    { enabled: !!token && groupIds.length >= 2 },
  )
}

export const useCreateItemCodeGroup = () => {
  const token = checkAuth()
  return useMutation((data: TCreateItemCodeGroup) => createItemCodeGroup(data, token), {
    onSuccess: (res: TResApi<TItemCodeGroup>) => {
      invalidateGroupList()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: notifyApiError,
  })
}

export const useUpdateItemCodeGroup = () => {
  const token = checkAuth()
  return useMutation(
    ({ id, data }: { id: number; data: TUpdateItemCodeGroup }) =>
      updateItemCodeGroup(id, data, token),
    {
      onSuccess: (res: TResApi<TItemCodeGroup>, { id }) => {
        invalidateGroupList()
        queryClient.invalidateQueries([ITEM_CODE_GROUP_DETAIL, id])
        notification.success({ message: NSuccess, description: res?.message })
      },
      // The 409 basis-lock conflict is shown inline by the form modal (its message already
      // explains the clone-policy suggestion, AC8) — every other error still gets the toast.
      onError: (error: TResApiErr & { status?: number }) => {
        if (error?.status !== 409) notifyApiError(error)
      },
    },
  )
}

export const useDeleteItemCodeGroup = () => {
  const token = checkAuth()
  return useMutation((id: number) => deleteItemCodeGroup(id, token), {
    onSuccess: (res: TResApi<unknown>) => {
      invalidateGroupList()
      notification.success({ message: NSuccess, description: res?.message })
    },
    // The 409 lock conflict is shown by the caller as a dedicated modal listing
    // usedByPolicyVersions (AC7) — a generic toast here would just duplicate/flatten it.
    onError: (error: TResApiErr & { status?: number }) => {
      if (error?.status !== 409) notifyApiError(error)
    },
  })
}

export const useUpdateItemCodeGroupMembers = (id: number | undefined) => {
  const token = checkAuth()
  return useMutation(
    (data: TPutItemCodeGroupMembers) => putItemCodeGroupMembers(id as number, data, token),
    {
      onSuccess: (res: TResApi<TPutItemCodeGroupMembersResult>) => {
        if (id) invalidateAfterMemberWrite(id)
        notification.success({ message: NSuccess, description: res?.message })
      },
      onError: notifyApiError,
    },
  )
}
